import React, { createContext, useContext, useCallback, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * NavigationContext
 * Centralized navigation state management for the PWA.
 * Tracks overlay stack (modals, drawers, sheets) and provides
 * intelligent back behavior that prioritizes closing overlays
 * before navigating to previous pages.
 */

const NavigationContext = createContext(null);

// Overlay priority (higher = closed first on back)
export const OVERLAY_PRIORITY = {
  IMAGE_ZOOM: 100,
  ADDRESS_PICKER: 90,
  AUTH_MODAL: 80,
  FILTER_DRAWER: 70,
  CART_SIDEBAR: 60,
  BOTTOM_SHEET: 50,
  CONFIRM_DIALOG: 40,
};

export function NavigationProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const overlayStackRef = useRef([]);
  const [overlayStack, setOverlayStack] = useState([]);
  const navigationGuardsRef = useRef([]);
  const scrollPositionsRef = useRef({});

  /**
   * Register an overlay (modal, drawer, sheet) with the navigation system.
   * When back is pressed, the highest-priority overlay is closed first.
   */
  const registerOverlay = useCallback((id, priority, closeFn) => {
    const entry = { id, priority, close: closeFn };
    overlayStackRef.current = [
      ...overlayStackRef.current.filter(o => o.id !== id),
      entry,
    ];
    setOverlayStack([...overlayStackRef.current]);
    // Push history state so back button triggers popstate
    // Use try/catch to handle browsers that limit pushState calls
    try {
      window.history.pushState({ overlay: id }, '');
    } catch (e) {
      // SecurityError or quota exceeded — safe to ignore
    }
  }, []);

  /**
   * Unregister an overlay when it's closed programmatically (not via back).
   */
  const unregisterOverlay = useCallback((id) => {
    overlayStackRef.current = overlayStackRef.current.filter(o => o.id !== id);
    setOverlayStack([...overlayStackRef.current]);
  }, []);

  /**
   * Check if any overlay is currently open.
   */
  const hasOpenOverlay = useCallback(() => {
    return overlayStackRef.current.length > 0;
  }, []);

  /**
   * Close the highest-priority overlay.
   * Returns true if an overlay was closed, false otherwise.
   */
  const closeTopOverlay = useCallback(() => {
    if (overlayStackRef.current.length === 0) return false;
    // Sort by priority descending, close the top one
    const sorted = [...overlayStackRef.current].sort((a, b) => b.priority - a.priority);
    const top = sorted[0];
    top.close();
    overlayStackRef.current = overlayStackRef.current.filter(o => o.id !== top.id);
    setOverlayStack([...overlayStackRef.current]);
    return true;
  }, []);

  /**
   * Register a navigation guard (e.g., unsaved changes warning).
   * Returns an unregister function.
   */
  const registerGuard = useCallback((id, checkFn, message) => {
    const guard = { id, check: checkFn, message };
    navigationGuardsRef.current = [
      ...navigationGuardsRef.current.filter(g => g.id !== id),
      guard,
    ];
    return () => {
      navigationGuardsRef.current = navigationGuardsRef.current.filter(g => g.id !== id);
    };
  }, []);

  /**
   * Check all guards before navigating.
   * Returns true if navigation should proceed, false if blocked.
   */
  const checkGuards = useCallback(() => {
    for (const guard of navigationGuardsRef.current) {
      if (guard.check()) {
        // Guard is active — show confirmation
        const shouldLeave = window.confirm(guard.message || 'You have unsaved changes. Are you sure you want to leave?');
        if (!shouldLeave) return false;
      }
    }
    return true;
  }, []);

  /**
   * Save scroll position for current route.
   */
  const saveScrollPosition = useCallback((path) => {
    const key = path || location.pathname;
    scrollPositionsRef.current[key] = window.scrollY;
  }, [location.pathname]);

  /**
   * Restore scroll position for a route.
   */
  const restoreScrollPosition = useCallback((path) => {
    const key = path || location.pathname;
    const pos = scrollPositionsRef.current[key];
    if (pos !== undefined) {
      requestAnimationFrame(() => {
        window.scrollTo(0, pos);
      });
    }
  }, [location.pathname]);

  /**
   * Navigate back with intelligent behavior:
   * 1. Close overlay if open
   * 2. Check guards
   * 3. Navigate to previous page or fallback
   */
  const goBack = useCallback((fallback = '/') => {
    // Priority 1: Close overlay
    if (closeTopOverlay()) return;

    // Priority 2: Check guards
    if (!checkGuards()) return;

    // Priority 3: Navigate back in history or to fallback
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(fallback, { replace: true });
    }
  }, [closeTopOverlay, checkGuards, navigate]);

  /**
   * Navigate to a route with scroll position saving.
   */
  const navigateTo = useCallback((path, options = {}) => {
    if (!checkGuards()) return;
    saveScrollPosition();
    navigate(path, options);
  }, [checkGuards, saveScrollPosition, navigate]);

  const value = {
    // Overlay management
    registerOverlay,
    unregisterOverlay,
    hasOpenOverlay,
    closeTopOverlay,
    overlayStack,
    // Navigation guards
    registerGuard,
    checkGuards,
    // Scroll restoration
    saveScrollPosition,
    restoreScrollPosition,
    // Navigation
    goBack,
    navigateTo,
    // Router state
    location,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const ctx = useContext(NavigationContext);
  if (!ctx) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return ctx;
}

export default NavigationContext;
